// Horses List Page
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { fetchHorses, fetchTenant } from '../../lib/queryFunctions';
import { useAuth } from '../../context/AuthContext';
import OnboardingBeacon from '../../components/onboarding/OnboardingBeacon';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, ChevronRight, Gavel } from 'lucide-react';
import { TableToolbar } from '../../components/common/TableToolbar';
import HorseshoeIcon from '../../components/icons/HorseshoeIcon';

// Horse icon (for header/decorative use)
const HorseIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 8.5c0-.8-.3-1.5-.9-2.1-.6-.6-1.3-.9-2.1-.9s-1.5.3-2.1.9L14 9.3V3L8 1 3 6l5 2v3c0 3.3 2.7 6 6 6h3l2.5 4L22 18" />
    <path d="M18 4v4" />
    <circle cx="18" cy="6" r="1" />
  </svg>
);

const colorLabels = {
  bay: 'Bay',
  black: 'Black',
  chestnut: 'Chestnut',
  dark_bay_brown: 'Dark Bay/Brown',
  gray_roan: 'Gray/Roan',
  palomino: 'Palomino',
  white: 'White',
};

const HORSES_PARAMS = { include_archived: true };

const HorsesListPage = () => {
  const { isViewOnly, isTenantAdmin, isSystemOwner, user } = useAuth();
  const viewOnly = isViewOnly();
  const isAdmin = isTenantAdmin() || isSystemOwner();
  const navigate = useNavigate();

  // ── Server state ──────────────────────────────────────────────────────────
  const { data: horses = [], isLoading } = useQuery({
    queryKey: QK.horses(HORSES_PARAMS),
    queryFn: fetchHorses(HORSES_PARAMS),
    onError: () => toast.error('Failed to load horses'),
  });

  const { data: tenant } = useQuery({
    queryKey: QK.tenant(user?.tenant_id),
    queryFn: fetchTenant(user?.tenant_id),
    enabled: !!user?.tenant_id,
  });
  const isFarm = tenant?.is_farm === true;

  // ── UI state ──────────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSex, setFilterSex] = useState('all');
  const [filterAge, setFilterAge] = useState('all');
  const [filterColor, setFilterColor] = useState('all');
  const [filterSaleStatus, setFilterSaleStatus] = useState('all');
  const [filterOwner, setFilterOwner] = useState('');
  const [filterOnFarm, setFilterOnFarm] = useState('all');
  const [hideArchived, setHideArchived] = useState(true);

  // ── Derived / filtered ────────────────────────────────────────────────────
  const filteredHorses = useMemo(() => horses.filter((horse) => {
    const matchesSearch =
      horse.system_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      horse.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      horse.dam_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      horse.sire_name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesSex        = filterSex === 'all' || horse.sex === filterSex;
    const matchesAge        = filterAge === 'all' || horse.age === filterAge;
    const matchesColor      = filterColor === 'all' || horse.color === filterColor;
    const matchesOwner      = !filterOwner ||
      (horse.owner_names || []).some(n => n.toLowerCase().includes(filterOwner.toLowerCase()));
    const matchesSaleStatus =
      filterSaleStatus === 'all' ||
      (filterSaleStatus === 'prospect'    && horse.sale_status === 'Sale Prospect') ||
      (filterSaleStatus === 'opportunity' && horse.sale_status === 'Sale Opportunity') ||
      (filterSaleStatus === 'confirmed'   && horse.sale_status === 'Sale Entry Confirmed') ||
      (filterSaleStatus === 'not_for_sale' && horse.sale_status === 'Not for Sale');
    const matchesArchived   = !hideArchived || !horse.is_archived;
    const matchesOnFarm     =
      filterOnFarm === 'all' ||
      (filterOnFarm === 'yes' && horse.boarding_type === 'on_farm') ||
      (filterOnFarm === 'no'  && horse.boarding_type !== 'on_farm');

    return matchesSearch && matchesSex && matchesAge && matchesColor &&
           matchesOwner && matchesSaleStatus && matchesArchived && matchesOnFarm;
  }).sort((a, b) => {
    const nameA = (a.system_name || a.name || '').toLowerCase();
    const nameB = (b.system_name || b.name || '').toLowerCase();
    return nameA.localeCompare(nameB);
  }), [horses, searchTerm, filterSex, filterAge, filterColor, filterOwner,
       filterSaleStatus, hideArchived, filterOnFarm]);

  // Sorted distinct age values — Suckling → Yearling → numeric → other
  const distinctAges = useMemo(() => [
    ...new Set(horses.map(h => h.age).filter(Boolean))
  ].sort((a, b) => {
    const order = ['Suckling', 'Yearling'];
    const ia = order.indexOf(a), ib = order.indexOf(b);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    const na = parseInt(a, 10), nb = parseInt(b, 10);
    if (!isNaN(na) && !isNaN(nb)) return na - nb;
    return a.localeCompare(b);
  }), [horses]);

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <SidebarLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="space-y-6" data-testid="horses-list-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl font-semibold">Horses</h1>
            <p className="text-muted-foreground mt-1">All Horses</p>
          </div>
          <div className="flex flex-col gap-2 items-end">
            {isAdmin && horses.some(h => h.is_archived) && (
              <Button
                variant={hideArchived ? 'outline' : 'secondary'}
                size="sm"
                className="h-8 text-xs gap-1.5"
                onClick={() => setHideArchived(h => !h)}
                data-testid="toggle-archived-horses"
              >
                {hideArchived ? 'Show Archived' : 'Hide Archived'}
              </Button>
            )}
            {!viewOnly && (
              <OnboardingBeacon itemKey="add_horse">
                <Button
                  onClick={() => navigate('/horses/new')}
                  className="btn-primary w-full sm:w-auto"
                  data-testid="new-horse-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Horse
                </Button>
              </OnboardingBeacon>
            )}
          </div>
        </div>

        <TableToolbar
          tableId="horses-list"
          data={filteredHorses}
          filename="horses"
          columns={[
            { key: 'system_name', label: 'Horse' },
            { key: 'age', label: 'Age' },
            { key: 'gender', label: 'Gender' },
            { key: 'color', label: 'Color', accessor: r => r.color?.replace(/_/g, ' ') },
            { key: 'sire_name', label: 'Sire' },
            { key: 'dam_name', label: 'Dam' },
            { key: 'sale_status', label: 'Sale Status' },
          ]}
        />

        {/* Table with Integrated Filters */}
        <Card>
          <Table className="table-heritage" data-table-id="horses-list">
            <TableHeader>
              <TableRow>
                {/* Horse Column with Search */}
                <TableHead className="min-w-[250px] align-top bg-muted/20">
                  <div className="flex flex-col gap-2 py-2">
                    <span className="font-bold text-foreground text-sm uppercase">Horse</span>
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                      <Input
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="h-8 pl-8 text-xs bg-white"
                        data-testid="search-horses-input"
                      />
                    </div>
                  </div>
                </TableHead>

                {/* Age / Gender Column */}
                <TableHead className="min-w-[180px] align-top bg-muted/20">
                  <div className="flex flex-col gap-2 py-2">
                    <span className="font-bold text-foreground text-sm uppercase">Age / Gender</span>
                    <Select value={filterAge} onValueChange={setFilterAge}>
                      <SelectTrigger className="h-8 text-xs bg-white" data-testid="filter-age-select">
                        <SelectValue placeholder="All Ages" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Ages</SelectItem>
                        {distinctAges.map(age => (
                          <SelectItem key={age} value={age}>{age}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={filterSex} onValueChange={setFilterSex}>
                      <SelectTrigger className="h-8 text-xs bg-white" data-testid="filter-sex-select">
                        <SelectValue placeholder="All Genders" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Genders</SelectItem>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </TableHead>

                {/* Color Column */}
                <TableHead className="min-w-[150px] align-top bg-muted/20">
                  <div className="flex flex-col gap-2 py-2">
                    <span className="font-bold text-foreground text-sm uppercase">Color</span>
                    <Select value={filterColor} onValueChange={setFilterColor}>
                      <SelectTrigger className="h-8 text-xs bg-white" data-testid="filter-color-select">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="bay">Bay</SelectItem>
                        <SelectItem value="black">Black</SelectItem>
                        <SelectItem value="chestnut">Chestnut</SelectItem>
                        <SelectItem value="dark_bay_brown">Dark Bay/Brown</SelectItem>
                        <SelectItem value="gray_roan">Gray/Roan</SelectItem>
                        <SelectItem value="palomino">Palomino</SelectItem>
                        <SelectItem value="white">White</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </TableHead>

                {/* Owner(s) Column */}
                <TableHead className="min-w-[180px] align-top bg-muted/20">
                  <div className="flex flex-col gap-2 py-2">
                    <span className="font-bold text-foreground text-sm uppercase">Owner(s)</span>
                    <Input
                      placeholder="Filter owners…"
                      value={filterOwner}
                      onChange={(e) => setFilterOwner(e.target.value)}
                      className="h-8 text-xs bg-white"
                      data-testid="filter-owner-input"
                    />
                  </div>
                </TableHead>

                {/* Sale Status Column */}
                <TableHead className="min-w-[180px] align-top bg-muted/20">
                  <div className="flex flex-col gap-2 py-2">
                    <span className="font-bold text-foreground text-sm uppercase">Sale Status</span>
                    <Select value={filterSaleStatus} onValueChange={setFilterSaleStatus}>
                      <SelectTrigger className="h-8 text-xs bg-white" data-testid="filter-sale-status-select">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="prospect">Sale Prospect</SelectItem>
                        <SelectItem value="opportunity">Sale Opportunity</SelectItem>
                        <SelectItem value="confirmed">Sale Entry Confirmed</SelectItem>
                        <SelectItem value="not_for_sale">Not for Sale</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </TableHead>

                {/* On Farm Column — farm tenants only */}
                {isFarm && (
                  <TableHead className="min-w-[130px] align-top bg-muted/20">
                    <div className="flex flex-col gap-2 py-2">
                      <span className="font-bold text-foreground text-sm uppercase">On Farm</span>
                      <Select value={filterOnFarm} onValueChange={setFilterOnFarm}>
                        <SelectTrigger className="h-8 text-xs bg-white" data-testid="filter-on-farm-select">
                          <SelectValue placeholder="All" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </TableHead>
                )}

                <TableHead className="w-[50px] bg-muted/20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredHorses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isFarm ? 6 : 5} className="text-center py-12">
                    <HorseshoeIcon className="w-12 h-12 mx-auto mb-3 text-muted-foreground/20" />
                    <p className="text-muted-foreground">No horses found</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-4"
                      onClick={() => navigate('/horses/new')}
                    >
                      <Plus className="w-4 h-4 mr-2" /> Add First Horse
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                filteredHorses.map((horse) => (
                  <TableRow
                    key={horse.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/horses/${horse.id}`)}
                    data-testid={`horse-row-${horse.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-secondary flex items-center justify-center shrink-0 rounded-full overflow-hidden">
                          {horse.main_photo_url ? (
                            <img
                              src={horse.main_photo_url.startsWith('http')
                                ? horse.main_photo_url
                                : `${window.location.origin}${horse.main_photo_url}`}
                              alt={horse.system_name}
                              className="w-10 h-10 object-cover rounded-full"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <HorseshoeIcon className="w-5 h-5 text-muted-foreground" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5 flex-wrap font-medium">
                            <span>{horse.system_name || horse.name}</span>
                            {horse.is_archived && <Badge variant="outline" className="border-orange-400 text-orange-600 text-[10px]">Archived</Badge>}
                            {horse.is_top_horse && <Badge variant="default" className="bg-yellow-600 text-[10px]">Top Horse</Badge>}
                            {horse.on_layup && <Badge className="bg-red-600 text-white text-[10px]" data-testid={`horse-list-layup-badge-${horse.id}`}>LAYUP</Badge>}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm">{horse.age || '-'}</p>
                        <p className="text-xs text-muted-foreground">{horse.gender || '-'}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{colorLabels[horse.color] || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(horse.owner_names || []).length > 0
                          ? <span className="text-sm">{horse.owner_names.join(', ')}</span>
                          : <span className="text-sm text-muted-foreground">-</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      {horse.sale_status === 'Sale Opportunity' ? (
                        <Badge className="bg-primary/10 text-primary">
                          <Gavel className="w-3 h-3 mr-1" /> Sale Opp.
                        </Badge>
                      ) : horse.sale_status === 'Sale Entry Confirmed' ? (
                        <Badge className="bg-green-100 text-green-700">
                          <Gavel className="w-3 h-3 mr-1" /> Confirmed
                        </Badge>
                      ) : horse.sale_status === 'Sale Prospect' ? (
                        <Badge variant="outline" className="text-yellow-700 border-yellow-300">
                          Prospect
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    {isFarm && (
                      <TableCell>
                        <span className={`text-xs font-medium ${horse.boarding_type === 'on_farm' ? 'text-green-700' : 'text-muted-foreground'}`}>
                          {horse.boarding_type === 'on_farm' ? 'Yes' : 'No'}
                        </span>
                      </TableCell>
                    )}
                    <TableCell>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </SidebarLayout>
  );
};

export default HorsesListPage;
